import { Check, ChevronRight, Sparkles, MousePointer, Layers, TrendingUp, Users, Zap, Shield } from "lucide-react";

const HeroSection = ({ onOpenModal, onOpenSketch }) => {
  const benefits = [
    "Ügynökségi design, nem sablon",
    "Mobilon is ütős (sticky CTA)",
    "Konverzió fókuszú felépítés",
    "Szöveg + struktúra egyben",
    "Később könnyen módosítható"
  ];

  const bentoTiles = [
    { type: "headline", className: "col-span-2 row-span-2", content: "Hero szekció", icon: Sparkles, alt: "Weboldal készítés hero szekció" },
    { type: "cta", className: "col-span-1 row-span-1", content: "CTA gomb", icon: MousePointer, alt: "Ügyfélszerző call-to-action gomb" },
    { type: "steps", className: "col-span-1 row-span-2", content: "Lépések", icon: Layers, alt: "Honlap készítés folyamat lépései" },
    { type: "pricing", className: "col-span-1 row-span-1", content: "Árak", icon: TrendingUp, alt: "Weboldal készítés árak" },
    { type: "reference", className: "col-span-1 row-span-1", content: "Referencia", icon: Users, alt: "Referenciák és vélemények" },
    { type: "chips", className: "col-span-2 row-span-1", content: "Előnyök", icon: Zap, alt: "Ügyfélszerző oldal előnyei" },
    { type: "trust", className: "col-span-1 row-span-1", content: "Bizalom", icon: Shield, alt: "Megbízható honlap készítés" }
  ];

  return (
    <section data-testid="hero-section" className="relative bg-ink overflow-hidden pt-24 pb-12 md:pb-16" aria-label="Weboldal készítés főszekció">
      {/* Background effects */}
      <div className="absolute inset-0 grid-pattern opacity-50" aria-hidden="true" />
      <div className="blur-blob blur-blob-1" aria-hidden="true" />
      <div className="blur-blob blur-blob-2" aria-hidden="true" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div className="text-center lg:text-left">
            {/* LCP elem - fetchpriority="high" ekvivalens CSS-sel */}
            <h1 
              className="font-sora font-bold text-3xl sm:text-4xl lg:text-5xl xl:text-[3.5rem] text-on-dark leading-[1.1] tracking-tight animate-fade-in-up"
              style={{ contentVisibility: 'visible', containIntrinsicSize: 'auto 150px' }}
            >
              Prémium weboldal készítés, ami nem csak{" "}
              <span className="relative inline-block">
                <span className="relative z-10">"szép"</span>
                <span className="absolute inset-0 gradient-ribbon opacity-30 blur-xl -z-0" aria-hidden="true" />
              </span>
              , hanem{" "}
              <span className="text-gradient">ügyfelet is hoz</span>
            </h1>

            <p className="mt-6 text-base sm:text-lg text-on-dark/70 max-w-2xl mx-auto lg:mx-0 leading-relaxed animate-fade-in-up animate-delay-100">
              Szolgáltatóknak készítünk modern, konverzióra épített ügyfélszerző oldalakat: egy fókusz, egy üzenet, egy cselekvés.
              <br className="hidden sm:block" />
              Nem hetekig tartó honlap készítés, hanem egy kontrollált, átlátható folyamat — ügynökségi megjelenéssel.
            </p>

            {/* Benefits pills */}
            <div
              className="flex flex-wrap justify-center lg:justify-start gap-2 mt-8 animate-fade-in-up animate-delay-200"
              role="list"
              aria-label="Weboldal készítés előnyei"
            >
              {benefits.map((benefit, i) => (
                <span
                  key={i}
                  role="listitem"
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-on-dark/80 chip-hover"
                >
                  <Check size={14} className="mr-2 text-accent" aria-hidden="true" />
                  {benefit}
                </span>
              ))}
            </div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-10 justify-center lg:justify-start animate-fade-in-up animate-delay-300">
              <button
                onClick={onOpenModal}
                className="bg-gradient-to-r from-primary to-accent text-white rounded-full px-8 py-4 font-semibold shadow-glow hover:shadow-glow-lg transition-all btn-glow text-base"
                data-testid="hero-primary-cta"
              >
                Kérek egy gyors ajánlatot
                <ChevronRight className="inline ml-2" size={18} />
              </button>
              <button
                onClick={onOpenSketch}
                className="bg-white/5 hover:bg-white/10 text-on-dark border border-white/10 rounded-full px-8 py-4 font-medium transition-all"
                data-testid="hero-secondary-cta"
              >
                Kérdésem van
              </button>
            </div>

            <p className="mt-6 text-sm text-on-dark/50 animate-fade-in-up animate-delay-400">
              Nem kell hosszú brief. Elég 4–5 mondat, és már tudunk irányt adni.
            </p>
          </div>

          {/* Right - Bento Grid */}
          <div className="hidden lg:grid grid-cols-3 grid-rows-4 gap-3 h-[500px] animate-fade-in-right">
            {bentoTiles.map((tile, i) => (
              <div
                key={i}
                className={`${tile.className} bento-tile bg-white/5 border border-white/10 rounded-2xl p-4 flex flex-col justify-between`}
                style={{ animationDelay: `${0.4 + i * 0.1}s` }}
              >
                <tile.icon className="text-accent" size={20} />
                <span className="text-xs text-on-dark/60 font-medium">{tile.content}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
